import useResponsive from 'hooks/useResponsive';
import { groupBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import MonthlyListViewCards from './MonthlyListViewCards';
import MonthlyListViewMobile from './MonthlyListViewMobile';
import MonthlyListViewTable from './MonthlyListViewTable';
import type { DayEvent } from './types';
import type { OrderActionsLog } from 'models/orderActionsLog';
import type { FC } from 'react';

interface MonthlyListViewProps {
  data: OrderActionsLog[];
  isLoading: boolean;
  selectedActions?: number[];
  dateRange: {
    start: Date;
    end: Date;
  };
}

const MonthlyListView: FC<MonthlyListViewProps> = ({
  data,
  selectedActions,
  dateRange,
}) => {
  const [selectedDay, setSelectedDay] = useState<DayEvent | null>(null);

  const isLtSmScreen = useResponsive('down', 'sm');
  const isLtLgScreen = useResponsive('down', 'lg');

  useEffect(() => {
    setSelectedDay(null);
  }, [selectedActions, dateRange.start, dateRange.end]);

  const groupedByMonthAndDay = useMemo(() => {
    const daysByDate: Record<string, DayEvent> = {};
    const currentDate = new Date(dateRange.start);

    while (currentDate <= dateRange.end) {
      const dateKey = currentDate.toISOString().split('T')[0] ?? '';
      daysByDate[dateKey] = {
        date: new Date(currentDate),
        events: [],
      };
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const eventsByDate = data.filter((event) => event.dateAction) as Array<
      OrderActionsLog & { dateAction: Date }
    >;

    eventsByDate.forEach((event) => {
      const date = new Date(event.dateAction);
      date.setHours(0, 0, 0, 0);
      const dateKey = date.toISOString().split('T')[0] ?? '';

      if (daysByDate[dateKey]) {
        daysByDate[dateKey]?.events.push(event);
      }
    });

    const daysWithEvents = Object.values(daysByDate).sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );

    const byMonth = groupBy(daysWithEvents, (day) => {
      const date = day.date;
      return `${date.getFullYear()}-${date.getMonth()}`;
    });

    return Object.entries(byMonth)
      .map(([monthKey, days]) => {
        const [year, month] = monthKey.split('-').map(Number);
        const monthDate = new Date(year ?? 0, month ?? 0, 1);
        return {
          month: monthDate,
          days: days.sort((a, b) => a.date.getTime() - b.date.getTime()),
        };
      })
      .sort((a, b) => a.month.getTime() - b.month.getTime());
  }, [data, dateRange]);

  const handleDayClick = (day: DayEvent): void => {
    setSelectedDay(day);
  };

  const ViewComponent = isLtSmScreen
    ? MonthlyListViewMobile
    : isLtLgScreen
    ? MonthlyListViewCards
    : MonthlyListViewTable;

  return (
    <ViewComponent
      groupedByMonthAndDay={groupedByMonthAndDay}
      selectedDay={selectedDay}
      handleDayClick={handleDayClick}
    />
  );
};

export default MonthlyListView;
